/**
 * OrgStructurePage – Hub de Estrutura Organizacional (Tela Única Master-Detail)
 *
 * Layout:
 *   ┌────────────────────────────────┬───────────────────────┐
 *   │  ÁRVORE INTERATIVA (60%)       │  PAINEL CONTEXTUAL    │
 *   │  Organograma vivo construído   │  (40%)                │
 *   │  da relação reports_to_user_id │  - Sem seleção: visão │
 *   │  cada nó: cargo · depto · user │    geral + ações       │
 *   │                                │  - Nó: edição inline   │
 *   └────────────────────────────────┴───────────────────────┘
 *
 * Multi-tenant: todas as queries filtram por organization_id.
 * SSOT: organizationStructureService + useOrgMembers + reports_to_user_id.
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Users, Plus, Trash2, AlertTriangle, Briefcase, Building2,
  ChevronRight, ChevronDown, X, UserCircle2, Network, Search,
  Pencil, Check, Save,
} from 'lucide-react';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { useOrgStructure } from '../../../hooks/useOrgStructure';
import { useOrgMembers, type OrgMember } from '../../../hooks/useOrgMembers';
import { hasCapability } from '../../../permissions';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { ModuleLoadingState } from '../../shared/components/ModulePageHeader';
import {
  createDepartment,
  updateDepartment,
  deleteDepartment,
  createRole,
  updateRole,
  deleteRole,
  setMemberPrimaryAssignment,
  type OrgDepartment,
  type OrgRole,
} from '../../../services/organizationStructureService';
import {
  requiresDirectManager,
  updateHierarchy,
  validateOrgMember,
  ROLE_TYPE_OPTIONS,
  type RoleType,
} from '../../../services/organizationService';

/* ════════════════════════════════════════════════════════════════
 *  SCOPE BADGE — maps role_type → data-scope label
 * ════════════════════════════════════════════════════════════════ */
type DataScopeKey = 'global' | 'department' | 'team' | 'individual';

const ROLE_TYPE_TO_SCOPE: Record<string, DataScopeKey> = {
  director:    'global',
  manager:     'department',
  lead:        'team',
  operational: 'individual',
};

const SCOPE_META: Record<DataScopeKey, { label: string; cls: string }> = {
  global:     { label: 'Global',     cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
  department: { label: 'Depto',      cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
  team:       { label: 'Equipe',     cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  individual: { label: 'Individual', cls: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400' },
};

function memberDataScope(roleType: OrgMember['roleType']): DataScopeKey {
  return ROLE_TYPE_TO_SCOPE[roleType ?? 'operational'] ?? 'individual';
}

/* ════════════════════════════════════════════════════════════════
 *  CPF INDICATOR — detect CPF-only accounts (no real email)
 * ════════════════════════════════════════════════════════════════ */
const CPF_REGEX = /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/;

function isCpfAccount(email: string): boolean {
  if (!email) return true;
  // No @ → raw CPF string
  if (!email.includes('@')) return CPF_REGEX.test(email.replace(/\s/g, ''));
  // Virtual CPF domain patterns
  return email.includes('@cpf.') || email.includes('@noreply.');
}

/** Returns the compact secondary line for a member card. */
function memberSubline(member: OrgMember): { text: string; isCpf: boolean } {
  if (isCpfAccount(member.email)) {
    const raw = member.email.replace(/@.*/u, '').replace(/[^\d.-]/gu, '');
    const cpf = raw || member.email;
    return { text: `CPF: ${cpf}`, isCpf: true };
  }
  return { text: member.email, isCpf: false };
}

/** Validates that a string is a non-empty UUID (prevents sending "" to Supabase). */
function isValidId(v: string): boolean {
  return v.length > 0 && /^[0-9a-f-]{8,}$/i.test(v);
}

/* ════════════════════════════════════════════════════════════════
 *  TYPES
 * ════════════════════════════════════════════════════════════════ */
interface HierarchyNode {
  member: OrgMember;
  department: OrgDepartment | null;
  role: OrgRole | null;
  children: HierarchyNode[];
  depth: number;
}

type ModalKind = 'department' | 'role' | 'member' | null;

/* ════════════════════════════════════════════════════════════════
 *  PAGE
 * ════════════════════════════════════════════════════════════════ */
export function OrgStructurePage() {
  const { workspace } = useWorkspace();
  const organizationId = workspace?.organizationId ?? '';
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const { departments, roles, isLoading: structureLoading, invalidate: invalidateStructure } =
    useOrgStructure(organizationId);
  const { members, refresh: refreshMembers } = useOrgMembers(organizationId);

  const roleCtx = workspace?.userRole ? { role: workspace.userRole } : null;
  const canManage =
    hasCapability(roleCtx, 'org_structure.manage', { realRoleOnly: true })
    || hasCapability(roleCtx, 'users.manage', { realRoleOnly: true })
    || hasCapability(roleCtx, 'org.manage', { realRoleOnly: true });

  // Lookup maps
  const deptById = useMemo(() => new Map(departments.map(d => [d.id, d])), [departments]);
  const roleById = useMemo(() => new Map(roles.map(r => [r.id, r])), [roles]);
  const memberById = useMemo(() => new Map(members.map(m => [m.id, m])), [members]);
  // Build recursive tree from reports_to_user_id
  const tree = useMemo<HierarchyNode[]>(() => {
    const nodes = new Map<string, HierarchyNode>();
    for (const m of members) {
      nodes.set(m.id, {
        member: m,
        department: m.departmentId ? deptById.get(m.departmentId) ?? null : null,
        role: m.roleId ? roleById.get(m.roleId) ?? null : null,
        children: [],
        depth: 0,
      });
    }
    const roots: HierarchyNode[] = [];
    for (const node of nodes.values()) {
      const parentId = node.member.reportsToUserId;
      if (parentId && parentId !== node.member.id && nodes.has(parentId)) {
        nodes.get(parentId)!.children.push(node);
      } else {
        roots.push(node);
      }
    }
    const fixDepth = (n: HierarchyNode, depth: number) => {
      n.depth = depth;
      n.children.sort((a, b) => a.member.name.localeCompare(b.member.name, 'pt-BR'));
      n.children.forEach(c => fixDepth(c, depth + 1));
    };
    roots.sort((a, b) => a.member.name.localeCompare(b.member.name, 'pt-BR'));
    roots.forEach(r => fixDepth(r, 0));
    return roots;
  }, [members, deptById, roleById]);

  // Selection + search state
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const posicionarEmail = searchParams.get('posicionar');

  useEffect(() => {
    const membro = searchParams.get('membro');
    if (membro && memberById.has(membro)) {
      setSelectedUserId(membro);
      return;
    }
    if (posicionarEmail) {
      const needle = posicionarEmail.toLowerCase().trim();
      const match = members.find(
        m => m.email.toLowerCase() === needle
          || m.email.toLowerCase().startsWith(needle.split('@')[0]),
      );
      if (match) setSelectedUserId(match.id);
    }
  }, [searchParams, memberById, members, posicionarEmail]);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<ModalKind>(null);
  const [error, setError] = useState<string | null>(null);

  const selected = selectedUserId ? memberById.get(selectedUserId) ?? null : null;

  const refreshAll = useCallback(async () => {
    await Promise.all([
      refreshMembers(),
      queryClient.invalidateQueries({ queryKey: ['org-structure', organizationId] }),
      queryClient.invalidateQueries({ queryKey: ['admin-users-table', organizationId] }),
      queryClient.invalidateQueries({ queryKey: ['org-hierarchy-members', organizationId] }),
    ]);
    await invalidateStructure();
  }, [refreshMembers, invalidateStructure, queryClient, organizationId]);

  const handleSaveMemberStructure = useCallback(async (payload: {
    departmentId: string;
    roleId: string;
    reportsToUserId: string | null;
    roleType: RoleType;
  }) => {
    if (!selectedUserId) return;
    const check = validateOrgMember({
      department_id: payload.departmentId,
      role_id: payload.roleId,
      role_type: payload.roleType,
      reports_to_user_id: payload.reportsToUserId,
    });
    if (!check.isComplete) {
      throw new Error(`Preencha: ${check.missing.join(', ')}`);
    }
    await updateHierarchy(organizationId, selectedUserId, {
      department_id: payload.departmentId,
      role_id: payload.roleId,
      reports_to_user_id: payload.reportsToUserId,
      role_type: payload.roleType,
    });
    await refreshAll();
    setError(null);
  }, [selectedUserId, organizationId, refreshAll]);

  /* ─── Render ─────────────────────────────────────────────────── */
  return (
    <RequireCapability capability="org_structure.view" realRoleOnly>
      <div className="enterprise-page h-full overflow-hidden flex flex-col px-[var(--shell-content-inset,10px)]">
        {/* Header */}
        <header className="mb-4 flex items-end justify-between gap-4">
          <div>
            <h2 className="text-[15px] font-semibold enterprise-text-primary flex items-center gap-2">
              <Network size={16} className="text-blue-600 dark:text-blue-400" />
              Estrutura Organizacional
            </h2>
            <p className="text-[12px] enterprise-text-secondary mt-0.5">
              Organograma interativo · clique em um nó para editar vínculos e cadeia de subordinação.
            </p>
          </div>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar pessoa…"
              className="w-56 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 pl-7 pr-2 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500/40"
            />
          </div>
        </header>

        {error && (
          <div className="mb-3 flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
            <AlertTriangle size={13} /> {error}
            <button onClick={() => setError(null)} className="ml-auto hover:opacity-70"><X size={12} /></button>
          </div>
        )}

        {posicionarEmail && !selected && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-sky-200/80 bg-sky-50/90 px-3 py-2.5 text-[12px] text-sky-800 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200">
            <Network size={14} className="shrink-0 mt-0.5" />
            <span>
              Convite enviado para <strong className="font-medium">{posicionarEmail}</strong>.
              Quando o usuário aceitar, ele aparecerá na árvore — use a busca ou atualize a página para posicioná-lo.
            </span>
          </div>
        )}

        {/* Split layout */}
        <div className="flex-1 grid min-h-0 grid-cols-1 gap-4 md:grid-cols-5">
          {/* LEFT 60% — Tree */}
          <section className="col-span-1 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30 p-3 overflow-y-auto md:col-span-3">
            {structureLoading ? (
              <ModuleLoadingState label="Carregando organograma" />
            ) : tree.length === 0 ? (
              <EmptyTree onCreateMember={() => canManage && setModal('member')} canManage={canManage} />
            ) : (
              <TreeView
                nodes={tree}
                selectedId={selectedUserId}
                onSelect={setSelectedUserId}
                searchTerm={search.trim().toLowerCase()}
              />
            )}
          </section>

          {/* RIGHT 40% — Detail / Overview (form scrolls; Salvar fixo no rodapé) */}
          <aside className="col-span-1 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 flex flex-col min-h-0 overflow-hidden md:col-span-2">
            {selected ? (
              <NodeDetailPanel
                member={selected}
                departments={departments}
                roles={roles}
                members={members}
                canManage={canManage}
                onClose={() => setSelectedUserId(null)}
                onSave={handleSaveMemberStructure}
              />
            ) : (
              <div className="flex-1 overflow-y-auto p-4">
              <OverviewPanel
                memberCount={members.length}
                deptCount={departments.length}
                roleCount={roles.length}
                rootCount={tree.length}
                departments={departments}
                roles={roles}
                canManage={canManage}
                onCreateDepartment={() => setModal('department')}
                onCreateRole={() => setModal('role')}
                onCreateMember={() => setModal('member')}
                onDeleteDepartment={async (id) => {
                  if (!confirm('Excluir departamento? Esta ação não pode ser desfeita.')) return;
                  try { await deleteDepartment(id); await refreshAll(); }
                  catch (e) { setError(e instanceof Error ? e.message : 'Falha ao excluir.'); }
                }}
                onDeleteRole={async (id) => {
                  if (!confirm('Excluir cargo? Esta ação não pode ser desfeita.')) return;
                  try { await deleteRole(id); await refreshAll(); }
                  catch (e) { setError(e instanceof Error ? e.message : 'Falha ao excluir.'); }
                }}
                onRenameDepartment={async (id, name) => {
                  try { await updateDepartment(id, { name }); await refreshAll(); }
                  catch (e) { setError(e instanceof Error ? e.message : 'Falha ao renomear.'); }
                }}
                onRenameRole={async (id, name) => {
                  try { await updateRole(id, { name }); await refreshAll(); }
                  catch (e) { setError(e instanceof Error ? e.message : 'Falha ao renomear.'); }
                }}
              />
              </div>
            )}
          </aside>
        </div>

        {/* Modais */}
        {modal === 'department' && canManage && (
          <CreateDepartmentModal
            organizationId={organizationId}
            onClose={() => setModal(null)}
            onCreated={async () => { setModal(null); await refreshAll(); }}
          />
        )}
        {modal === 'role' && canManage && (
          <CreateRoleModal
            organizationId={organizationId}
            departments={departments}
            onClose={() => setModal(null)}
            onCreated={async () => { setModal(null); await refreshAll(); }}
          />
        )}
        {modal === 'member' && canManage && (
          <AssignMemberModal
            members={members}
            departments={departments}
            roles={roles}
            organizationId={organizationId}
            onClose={() => setModal(null)}
            onSaved={async () => { setModal(null); await refreshAll(); }}
          />
        )}
      </div>
    </RequireCapability>
  );
}

/* ════════════════════════════════════════════════════════════════
 *  TREE VIEW (left column)
 * ════════════════════════════════════════════════════════════════ */
interface TreeViewProps {
  nodes: HierarchyNode[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  searchTerm: string;
}

const TreeView: React.FC<TreeViewProps> = ({ nodes, selectedId, onSelect, searchTerm }) => (
  <div className="space-y-1">
    {nodes.map(n => (
      <TreeNode
        key={n.member.id}
        node={n}
        selectedId={selectedId}
        onSelect={onSelect}
        searchTerm={searchTerm}
      />
    ))}
  </div>
);

interface TreeNodeProps {
  node: HierarchyNode;
  selectedId: string | null;
  onSelect: (id: string) => void;
  searchTerm: string;
}

const TreeNode: React.FC<TreeNodeProps> = ({ node, selectedId, onSelect, searchTerm }) => {
  const [expanded, setExpanded] = useState(node.depth < 2);
  const hasChildren = node.children.length > 0;
  const isSelected = selectedId === node.member.id;
  const matches = !searchTerm || node.member.name.toLowerCase().includes(searchTerm)
    || (node.role?.name.toLowerCase().includes(searchTerm) ?? false)
    || (node.department?.name.toLowerCase().includes(searchTerm) ?? false);

  // If filtering and this branch has no matches, hide
  const branchHasMatch = useMemo(() => {
    if (!searchTerm) return true;
    const visit = (n: HierarchyNode): boolean =>
      n.member.name.toLowerCase().includes(searchTerm)
      || (n.role?.name.toLowerCase().includes(searchTerm) ?? false)
      || (n.department?.name.toLowerCase().includes(searchTerm) ?? false)
      || n.children.some(visit);
    return visit(node);
  }, [node, searchTerm]);

  if (!branchHasMatch) return null;

  return (
    <div style={{ paddingLeft: node.depth * 16 }}>
      <button
        type="button"
        onClick={() => onSelect(node.member.id)}
        className={`group w-full flex items-center gap-2 rounded-md border bg-white dark:bg-slate-950 px-2 py-1.5 mb-1 text-left transition-all shadow-sm hover:shadow ${
          isSelected
            ? 'border-blue-500 ring-1 ring-blue-500/20'
            : 'border-slate-100 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
        } ${!matches && searchTerm ? 'opacity-50' : ''}`}
      >
        {/* expand chevron */}
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); if (hasChildren) setExpanded(v => !v); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setExpanded(v => !v); } }}
          className={`inline-flex h-4 w-4 items-center justify-center rounded ${hasChildren ? 'hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer' : 'invisible'}`}
        >
          {hasChildren && (expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />)}
        </span>

        {/* avatar */}
        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500">
          <UserCircle2 size={14} />
        </span>

        {/* main label */}
        <span className="flex-1 min-w-0">
          <span className="block truncate text-[12.5px] font-medium enterprise-text-primary">
            {node.role?.name ?? <span className="text-slate-400 italic">Sem cargo</span>}
          </span>
          <span className="block truncate text-[11px] enterprise-text-secondary">
            {node.member.name}
          </span>
          {/* CPF indicator — compact, only for CPF-based accounts */}
          {(() => {
            const sub = memberSubline(node.member);
            return sub.isCpf ? (
              <span className="block truncate text-[10px] text-amber-600 dark:text-amber-400">
                👤 {sub.text}
              </span>
            ) : null;
          })()}
        </span>

        {/* data-scope badge derived from role_type */}
        {node.member.roleType && (() => {
          const scope = memberDataScope(node.member.roleType);
          const meta = SCOPE_META[scope];
          return (
            <span
              className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${meta.cls}`}
              title={`Data scope: ${scope}`}
            >
              {meta.label}
            </span>
          );
        })()}

        {/* department tag */}
        {node.department && (
          <span
            className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium text-white"
            style={{ backgroundColor: node.department.color }}
            title={node.department.name}
          >
            {node.department.name}
          </span>
        )}
      </button>

      {expanded && hasChildren && (
        <div>
          {node.children.map(c => (
            <TreeNode
              key={c.member.id}
              node={c}
              selectedId={selectedId}
              onSelect={onSelect}
              searchTerm={searchTerm}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const EmptyTree: React.FC<{ onCreateMember: () => void; canManage: boolean }> = ({ onCreateMember, canManage }) => (
  <div className="h-full flex flex-col items-center justify-center text-center px-6 py-10">
    <Network size={32} className="text-slate-300 mb-3" />
    <h3 className="text-[13px] font-semibold enterprise-text-primary">Sem organograma ainda</h3>
    <p className="text-[12px] enterprise-text-secondary mt-1 max-w-xs">
      Vincule membros a cargos e departamentos para que a hierarquia apareça aqui.
    </p>
    {canManage && (
      <button
        type="button"
        onClick={onCreateMember}
        className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-blue-700"
      >
        <Plus size={12} /> Vincular Membro
      </button>
    )}
  </div>
);

/* ════════════════════════════════════════════════════════════════
 *  CONTEXT PANEL — variants
 * ════════════════════════════════════════════════════════════════ */
interface OverviewPanelProps {
  memberCount: number;
  deptCount: number;
  roleCount: number;
  rootCount: number;
  departments: OrgDepartment[];
  roles: OrgRole[];
  canManage: boolean;
  onCreateDepartment: () => void;
  onCreateRole: () => void;
  onCreateMember: () => void;
  onDeleteDepartment: (id: string) => void;
  onDeleteRole: (id: string) => void;
  onRenameDepartment: (id: string, name: string) => Promise<void>;
  onRenameRole: (id: string, name: string) => Promise<void>;
}

const OverviewPanel: React.FC<OverviewPanelProps> = ({
  memberCount, deptCount, roleCount, rootCount,
  departments, roles, canManage,
  onCreateDepartment, onCreateRole, onCreateMember,
  onDeleteDepartment, onDeleteRole,
  onRenameDepartment, onRenameRole,
}) => (
  <div className="flex-1 flex flex-col gap-4">
    <div>
      <h3 className="text-[13px] font-semibold enterprise-text-primary">Visão Geral</h3>
      <p className="text-[11px] enterprise-text-secondary mt-0.5">
        Selecione um nó à esquerda para editar vínculos.
      </p>
    </div>

    {/* metrics */}
    <div className="grid grid-cols-2 gap-2">
      <MetricCard icon={Users} label="Membros" value={memberCount} />
      <MetricCard icon={Network} label="Raízes" value={rootCount} />
      <MetricCard icon={Building2} label="Departamentos" value={deptCount} />
      <MetricCard icon={Briefcase} label="Cargos" value={roleCount} />
    </div>

    {/* global actions */}
    {canManage && (
      <div className="grid grid-cols-1 gap-2">
        <ActionButton icon={Building2} label="Criar Departamento" onClick={onCreateDepartment} />
        <ActionButton icon={Briefcase} label="Criar Cargo" onClick={onCreateRole} />
        <ActionButton icon={Users} label="Vincular Membro" onClick={onCreateMember} primary />
      </div>
    )}

    {/* department list */}
    <Section title="Departamentos">
      {departments.length === 0 ? (
        <EmptyHint text="Nenhum departamento cadastrado." />
      ) : (
        <ul className="space-y-1">
          {departments.map(d => (
            <EditableRow
              key={d.id}
              initialValue={d.name}
              canManage={canManage}
              onSave={(v) => onRenameDepartment(d.id, v)}
              onDelete={() => onDeleteDepartment(d.id)}
              leading={<span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: d.color }} />}
            />
          ))}
        </ul>
      )}
    </Section>

    {/* role list */}
    <Section title="Cargos">
      {roles.length === 0 ? (
        <EmptyHint text="Nenhum cargo cadastrado." />
      ) : (
        <ul className="space-y-1 max-h-64 overflow-y-auto">
          {roles.map(r => (
            <EditableRow
              key={r.id}
              initialValue={r.name}
              canManage={canManage && !r.isSystem}
              onSave={(v) => onRenameRole(r.id, v)}
              onDelete={() => onDeleteRole(r.id)}
              leading={<Briefcase size={11} className="text-slate-400 shrink-0" />}
            />
          ))}
        </ul>
      )}
    </Section>
  </div>
);

interface NodeDetailPanelProps {
  member: OrgMember;
  departments: OrgDepartment[];
  roles: OrgRole[];
  members: OrgMember[];
  canManage: boolean;
  onClose: () => void;
  onSave: (payload: {
    departmentId: string;
    roleId: string;
    reportsToUserId: string | null;
    roleType: RoleType;
  }) => Promise<void>;
}

const NodeDetailPanel: React.FC<NodeDetailPanelProps> = ({
  member, departments, roles, members, canManage,
  onClose, onSave,
}) => {
  const [deptId, setDeptId] = useState<string>(member.departmentId ?? '');
  const [roleId, setRoleId] = useState<string>(member.roleId ?? '');
  const [managerId, setManagerId] = useState<string>(member.reportsToUserId ?? '');
  const [roleType, setRoleType] = useState<RoleType>(member.roleType ?? 'operational');
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const needsManager = requiresDirectManager(roleType);

  useEffect(() => {
    setDeptId(member.departmentId ?? '');
    setRoleId(member.roleId ?? '');
    setManagerId(member.reportsToUserId ?? '');
    setRoleType(member.roleType ?? 'operational');
    setLocalError(null);
    setSaveSuccess(false);
  }, [member.id, member.departmentId, member.roleId, member.reportsToUserId, member.roleType]);

  const rolesForDept = useMemo(() => {
    const scoped = roles.filter(r => !deptId || !r.departmentId || r.departmentId === deptId);
    return scoped.length > 0 ? scoped : roles;
  }, [roles, deptId]);

  const draftComplete = validateOrgMember({
    department_id: deptId || null,
    role_id: roleId || null,
    role_type: roleType,
    reports_to_user_id: managerId || null,
  }).isComplete;

  const handleSave = async () => {
    if (!isValidId(deptId) || !isValidId(roleId)) {
      setLocalError('Selecione departamento e cargo.');
      return;
    }
    if (needsManager && !isValidId(managerId)) {
      setLocalError('Selecione um gestor direto válido.');
      return;
    }
    setSaving(true);
    setLocalError(null);
    setSaveSuccess(false);
    try {
      await onSave({
        departmentId: deptId,
        roleId,
        reportsToUserId: managerId || null,
        roleType,
      });
      setSaveSuccess(true);
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Falha ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-4">
      {/* Header */}
      <header className="flex items-start gap-2 border-b border-slate-100 dark:border-slate-800 pb-3 shrink-0">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400">
          <UserCircle2 size={20} />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="text-[13px] font-semibold enterprise-text-primary truncate">{member.name}</h3>
          {(() => {
            const sub = memberSubline(member);
            return sub.isCpf ? (
              <p className="text-[11px] text-amber-600 dark:text-amber-400 truncate">
                👤 {sub.text}
              </p>
            ) : (
              <p className="text-[11px] enterprise-text-secondary truncate">{sub.text}</p>
            );
          })()}
          {/* Data scope chip in detail header */}
          {member.roleType && (() => {
            const scope = memberDataScope(member.roleType);
            const meta = SCOPE_META[scope];
            return (
              <span
                className={`inline-block mt-1 rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${meta.cls}`}
              >
                Escopo: {meta.label}
              </span>
            );
          })()}
        </div>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">
          <X size={14} />
        </button>
      </header>

      {/* Nível hierárquico */}
      <Field label="Nível hierárquico" icon={Users}>
        <select
          value={roleType}
          onChange={(e) => setRoleType(e.target.value as RoleType)}
          disabled={!canManage}
          className={selectClass}
        >
          {ROLE_TYPE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </Field>

      {/* Departamento */}
      <Field label="Departamento" icon={Building2}>
        <select
          value={deptId}
          onChange={(e) => { setDeptId(e.target.value); setRoleId(''); setSaveSuccess(false); }}
          disabled={!canManage}
          className={selectClass}
        >
          <option value="">— Selecione —</option>
          {departments.map(d => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
        {departments.length === 0 && (
          <p className="mt-1 text-[10px] text-amber-600">Nenhum departamento cadastrado. Crie um na visão geral (painel direito sem seleção).</p>
        )}
      </Field>

      {/* Cargo */}
      <Field label="Cargo" icon={Briefcase}>
        <select
          value={roleId}
          onChange={(e) => setRoleId(e.target.value)}
          disabled={!canManage || !deptId}
          className={selectClass}
        >
          <option value="">— Selecione —</option>
          {rolesForDept.map(r => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
        {roles.length === 0 && (
          <p className="mt-1 text-[10px] text-amber-600">Nenhum cargo cadastrado. Crie um cargo na visão geral.</p>
        )}
      </Field>

      {/* Reports to */}
      <Field label="Gestor direto" icon={Network}>
        <select
          value={managerId}
          onChange={(e) => setManagerId(e.target.value)}
          disabled={!canManage}
          className={`${selectClass} ${needsManager && !managerId ? 'border-amber-400/70' : ''}`}
        >
          <option value="">
            {needsManager ? '— Selecione o gestor —' : '— Nenhum (topo da hierarquia) —'}
          </option>
          {members
            .filter(m => m.id !== member.id)
            .map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
        </select>
        {needsManager && !managerId && (
          <p className="mt-1 text-[10px] text-amber-600 dark:text-amber-400">
            Analistas e demais níveis precisam de um gestor direto válido.
          </p>
        )}
      </Field>

      {localError && (
        <p className="text-[11px] text-rose-600 dark:text-rose-400 flex items-center gap-1">
          <AlertTriangle size={12} /> {localError}
        </p>
      )}
      {saveSuccess && (
        <p className="text-[11px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
          <Check size={12} /> Estrutura salva com sucesso.
        </p>
      )}

      </div>

      {/* Rodapé fixo — Salvar sempre visível */}
      <div className="shrink-0 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
        {canManage ? (
          <>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-3 py-2.5 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-wait transition-colors"
            >
              <Save size={15} />
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
            {!draftComplete && !saving && (
              <p className="mt-1.5 text-[10px] text-amber-600 dark:text-amber-400 text-center">
                Preencha departamento, cargo{needsManager ? ' e gestor direto' : ''} antes de salvar.
              </p>
            )}
          </>
        ) : (
          <p className="text-[11px] text-center enterprise-text-secondary">
            Você tem permissão apenas para visualizar a estrutura.
          </p>
        )}
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════
 *  REUSABLE BITS
 * ════════════════════════════════════════════════════════════════ */
const selectClass =
  'w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-2 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500/40 disabled:opacity-60 disabled:cursor-not-allowed';

const inputClass =
  'w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-2 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500/40';

const Field: React.FC<{ label: string; icon: React.ComponentType<{ size?: number; className?: string }>; children: React.ReactNode }> = ({ label, icon: Icon, children }) => (
  <div>
    <label className="mb-1 flex items-center gap-1.5 text-[11px] font-medium enterprise-text-secondary">
      <Icon size={11} /> {label}
    </label>
    {children}
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <h4 className="text-[11px] font-semibold uppercase tracking-wider enterprise-text-secondary mb-1.5">{title}</h4>
    {children}
  </div>
);

const EmptyHint: React.FC<{ text: string }> = ({ text }) => (
  <p className="text-[11px] enterprise-text-secondary italic">{text}</p>
);

const MetricCard: React.FC<{ icon: React.ComponentType<{ size?: number; className?: string }>; label: string; value: number }> = ({ icon: Icon, label, value }) => (
  <div className="rounded-md border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-950 px-2.5 py-2 shadow-sm">
    <div className="flex items-center gap-1.5 text-[10px] enterprise-text-secondary uppercase tracking-wider">
      <Icon size={11} /> {label}
    </div>
    <div className="text-[16px] font-semibold enterprise-text-primary mt-0.5">{value}</div>
  </div>
);

const ActionButton: React.FC<{
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  onClick: () => void;
  primary?: boolean;
}> = ({ icon: Icon, label, onClick, primary }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors ${
      primary
        ? 'bg-blue-600 text-white hover:bg-blue-700'
        : 'border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 enterprise-text-primary hover:bg-slate-50 dark:hover:bg-slate-900'
    }`}
  >
    <Icon size={12} /> {label}
  </button>
);

/* ─── Editable row (inline rename + delete) ─────────────────────── */
interface EditableRowProps {
  initialValue: string;
  canManage: boolean;
  onSave: (newName: string) => Promise<void>;
  onDelete: () => void;
  leading?: React.ReactNode;
}

const EditableRow: React.FC<EditableRowProps> = ({ initialValue, canManage, onSave, onDelete, leading }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initialValue);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => { setValue(initialValue); }, [initialValue]);

  const commit = async () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === initialValue) { setEditing(false); setValue(initialValue); return; }
    setBusy(true);
    try { await onSave(trimmed); setEditing(false); }
    finally { setBusy(false); }
  };

  const cancel = () => { setValue(initialValue); setEditing(false); };

  return (
    <li className="group flex items-center gap-2 rounded-md border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-950 px-2 py-1.5 shadow-sm">
      {leading}
      {editing ? (
        <input
          type="text"
          value={value}
          autoFocus
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          }}
          disabled={busy}
          className="flex-1 min-w-0 rounded border border-blue-300 dark:border-blue-700 bg-white dark:bg-slate-950 px-1.5 py-0.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500/40"
        />
      ) : (
        <span className="flex-1 truncate text-[12px] enterprise-text-primary">{initialValue}</span>
      )}
      {canManage && !editing && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="opacity-0 group-hover:opacity-100 hover:opacity-100 text-slate-400 hover:text-blue-600"
          title="Renomear"
        >
          <Pencil size={12} />
        </button>
      )}
      {canManage && editing && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault() /* keep input focus */}
          onClick={commit}
          disabled={busy}
          className="text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
          title="Salvar"
        >
          <Check size={12} />
        </button>
      )}
      {canManage && !editing && (
        <button
          type="button"
          onClick={onDelete}
          className="opacity-0 group-hover:opacity-100 hover:opacity-100 text-rose-500 hover:text-rose-700"
          title="Excluir"
        >
          <Trash2 size={12} />
        </button>
      )}
    </li>
  );
};

/* ════════════════════════════════════════════════════════════════
 *  MODALS
 * ════════════════════════════════════════════════════════════════ */
const ModalShell: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 backdrop-blur-sm p-4">
    <div className="w-full max-w-sm rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-xl">
      <header className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 px-4 py-2.5">
        <h3 className="text-[13px] font-semibold enterprise-text-primary">{title}</h3>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={14} /></button>
      </header>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  </div>
);

const PALETTE = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#06B6D4', '#64748B'];

const CreateDepartmentModal: React.FC<{
  organizationId: string;
  onClose: () => void;
  onCreated: () => Promise<void>;
}> = ({ organizationId, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState(PALETTE[0]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await createDepartment(organizationId, name.trim(), color);
      await onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Falha ao criar departamento.');
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Novo Departamento" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-[11px] font-medium enterprise-text-secondary mb-1">Nome</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Operações"
            className={inputClass}
            autoFocus
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium enterprise-text-secondary mb-1">Cor</label>
          <div className="flex flex-wrap gap-1.5">
            {PALETTE.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className={`h-6 w-6 rounded-full border-2 transition-transform ${color === c ? 'border-slate-800 dark:border-white scale-110' : 'border-transparent'}`}
                style={{ backgroundColor: c }}
                aria-label={c}
              />
            ))}
          </div>
        </div>
        {err && <div className="text-[11px] text-rose-600">{err}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-[12px] enterprise-text-primary hover:bg-slate-50 dark:hover:bg-slate-900">Cancelar</button>
          <button type="submit" disabled={!name.trim() || busy} className="rounded-md bg-blue-600 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {busy ? 'Criando…' : 'Criar'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

const CreateRoleModal: React.FC<{
  organizationId: string;
  departments: OrgDepartment[];
  onClose: () => void;
  onCreated: () => Promise<void>;
}> = ({ organizationId, departments, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [deptId, setDeptId] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await createRole(organizationId, name.trim(), deptId || null);
      await onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Falha ao criar cargo.');
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Novo Cargo" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-[11px] font-medium enterprise-text-secondary mb-1">Nome</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Coordenador de Operações"
            className={inputClass}
            autoFocus
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium enterprise-text-secondary mb-1">Departamento</label>
          <select value={deptId} onChange={(e) => setDeptId(e.target.value)} className={selectClass}>
            <option value="">— Sem departamento —</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        {err && <div className="text-[11px] text-rose-600">{err}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-[12px] enterprise-text-primary hover:bg-slate-50 dark:hover:bg-slate-900">Cancelar</button>
          <button type="submit" disabled={!name.trim() || busy} className="rounded-md bg-blue-600 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {busy ? 'Criando…' : 'Criar'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

const AssignMemberModal: React.FC<{
  organizationId: string;
  members: OrgMember[];
  departments: OrgDepartment[];
  roles: OrgRole[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}> = ({ organizationId, members, departments, roles, onClose, onSaved }) => {
  const [userId, setUserId] = useState('');
  const [deptId, setDeptId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const assignedSet = useMemo(
    () => new Set(members.filter(m => m.departmentId && m.roleId).map(m => m.id)),
    [members],
  );

  const rolesForDept = useMemo(
    () => roles.filter(r => !deptId || !r.departmentId || r.departmentId === deptId),
    [roles, deptId],
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !deptId || !roleId) return;
    setBusy(true);
    try {
      await setMemberPrimaryAssignment(organizationId, userId, deptId, roleId);
      await onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Falha ao vincular membro.');
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Vincular Membro" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-[11px] font-medium enterprise-text-secondary mb-1">Pessoa</label>
          <select value={userId} onChange={(e) => setUserId(e.target.value)} className={selectClass} autoFocus>
            <option value="">— Selecione —</option>
            {members.map(m => (
              <option key={m.id} value={m.id}>
                {m.name} {assignedSet.has(m.id) ? '· (substituir vínculo atual)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-medium enterprise-text-secondary mb-1">Departamento</label>
          <select value={deptId} onChange={(e) => setDeptId(e.target.value)} className={selectClass}>
            <option value="">— Selecione —</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-medium enterprise-text-secondary mb-1">Cargo</label>
          <select value={roleId} onChange={(e) => setRoleId(e.target.value)} className={selectClass} disabled={!deptId}>
            <option value="">— Selecione —</option>
            {rolesForDept.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        {err && <div className="text-[11px] text-rose-600">{err}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-[12px] enterprise-text-primary hover:bg-slate-50 dark:hover:bg-slate-900">Cancelar</button>
          <button type="submit" disabled={!userId || !deptId || !roleId || busy} className="rounded-md bg-blue-600 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {busy ? 'Salvando…' : 'Vincular'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

export default OrgStructurePage;
